import "./Overview.scss";

import * as R from "ramda";

import {
  BrandHealthTimeSeries,
  IncrementalRoasTakeaways,
} from "@blisspointmedia/bpm-types/dist/BrandEquity";
import { Channel1, Channel2, Channel3, Channel4 } from "../../utils/colors";
import {
  DATE_GROUPING_OPTIONS_BRAND_EQUITY,
  formatModelOverviewOutcomeVariable,
  groupChartDataByDate,
} from "../BrandEquityUtils";
import { DownloadDropdown, Dropdown, DropdownToggleType, InfoTooltip } from "../../Components";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { capitalizeWords, currencyFormatter, numberFormatter } from "../../MMM/MMMUtils";
import { downloadPNG, exportToExcel } from "../../utils/download-utils";
import {
  keyGeneratorForChannelTacticBrandGrouping,
  percFormat,
  standardizeMetricName,
} from "./BrandImpactUtils";

import AreaChart from "../../Components/Charts/AreaChart";
import ChartContainer from "../../Components/ChartContainer";
import { DateGroupingKey } from "../../TVADCrossChannel/homePageConstants";
import DynamicModelOverview from "../../Components/DynamicModelOverview/DynamicModelOverview";
import LineChart from "../../Components/Charts/LineChart";
import WidgetContainer from "../../Components/WidgetContainer";
import { dateToMddyy } from "../../utils/date-utils";
import { formatNumber } from "../../utils/format-utils";
import { useCompanyInfo } from "../../redux/company";

interface ModelOverview {
  client: string;
  kpi: string;
  kpi_type: string;
  date_granularity: string;
  refresh_date: string;
  refresh_timestamp: string;
  train_start_date: string;
  train_end_date: string;
  test_start_date: string;
  test_end_date: string;
  train_size: number;
  test_size: number;
  train_rsquared: number;
  test_rsquared: number;
  test_mape: number;
  test_mae: number;
  n_observations: number;
  mean_spend: number;
  median_spend: number;
}

interface WeeklyBrandHealthAreaChart {
  date: string;
  channel: string;
  platform?: string;
  tactic?: string;
  ["brand/nonbrand"]?: string;
  spend: number;
}

interface OverviewProps {
  company: string;
  groupByMetric: string;
  groupByLevel: string;
  weeklyBrandHealthTimeSeries: BrandHealthTimeSeries[];
  weeklyBrandHealthAreaChartSpend: Record<string, WeeklyBrandHealthAreaChart[]>;
  modelOverviewAll: Record<string, {}>;
  incrementalRoasTakeaways: IncrementalRoasTakeaways;
  isQv: boolean;
  queryVolumeLongLabel: string;
  queryVolumeShortLabel: string;
}

const colors = [Channel1, Channel3, Channel2, Channel4];

export const Overview: React.FC<OverviewProps> = ({
  company,
  groupByMetric,
  groupByLevel,
  weeklyBrandHealthTimeSeries,
  weeklyBrandHealthAreaChartSpend,
  modelOverviewAll,
  incrementalRoasTakeaways,
  isQv,
  queryVolumeLongLabel,
  queryVolumeShortLabel,
}) => {
  const groupByMetricName =
    groupByMetric.toLocaleUpperCase() === "GQV" ? queryVolumeShortLabel : groupByMetric;
  const [weeklyBrandHealthChartDateGrouping, setWeeklyBrandHealthChartDateGrouping] = useState(
    "Week"
  );
  const [weeklySpendChartDateGrouping, setWeeklySpendChartDateGrouping] = useState("Week");
  const { fiscal_cal: fiscalCal } = useCompanyInfo();
  const { res: weeklyBrandHealthTimeSeriesByDate } = useMemo(() => {
    if (weeklyBrandHealthTimeSeries.length > 0) {
      const groupByDateGqvData = groupChartDataByDate(
        weeklyBrandHealthTimeSeries,
        weeklyBrandHealthChartDateGrouping as DateGroupingKey,
        fiscalCal
      );
      return { res: groupByDateGqvData };
    }
    return { res: [] };
  }, [weeklyBrandHealthChartDateGrouping, weeklyBrandHealthTimeSeries, fiscalCal]);

  const keysWithoutDate = useMemo(
    () =>
      Object.keys(weeklyBrandHealthTimeSeries[0])
        .filter(key => key !== "date")
        .sort(),
    [weeklyBrandHealthTimeSeries]
  );

  const modelOverview = modelOverviewAll[groupByMetric] as ModelOverview;
  const [areaChartKeys, setAreaChartKeys] = useState<any[]>([]);

  const weeklyBrandHealthSpendAreaChart = useMemo(() => {
    if (Object.values(weeklyBrandHealthAreaChartSpend).length !== 0) {
      let finalAreaChart: any[] = [];
      switch (groupByLevel) {
        case "channel":
          finalAreaChart = weeklyBrandHealthAreaChartSpend[groupByMetric].reduce(
            (total, current) => {
              const existingRow = total.find(r => r.date === current.date);
              if (existingRow) {
                const levelName = current[groupByLevel];
                if (levelName in existingRow) {
                  existingRow[levelName] += current.spend;
                } else {
                  existingRow[levelName] = current.spend;
                }
              } else {
                const levelName = current[groupByLevel];
                total.push({ date: current.date, [levelName]: current.spend });
              }
              return total;
            },
            [] as any[]
          );
          break;
        case "channel, platform":
          finalAreaChart = weeklyBrandHealthAreaChartSpend[groupByMetric].reduce(
            (total, current) => {
              const existingRow = total.find(r => r.date === current.date);
              const key = keyGeneratorForChannelTacticBrandGrouping(
                current.channel,
                current.platform
              );
              if (existingRow) {
                if (key in existingRow) {
                  existingRow[key] += current.spend;
                } else {
                  existingRow[key] = current.spend;
                }
              } else {
                total.push({
                  date: current.date,
                  [key]: current.spend,
                });
              }
              return total;
            },
            [] as any[]
          );
          break;
        default:
          finalAreaChart = weeklyBrandHealthAreaChartSpend[groupByMetric].reduce(
            (total, current) => {
              const existingRow = total.find(r => r.date === current.date);

              const key = keyGeneratorForChannelTacticBrandGrouping(
                current.channel,
                current.platform,
                current.tactic || "",
                current["brand/nonbrand"] || ""
              );

              if (existingRow) {
                if (key in existingRow) {
                  existingRow[key] += current.spend;
                } else {
                  existingRow[key] = current.spend;
                }
              } else {
                total.push({
                  date: current.date,
                  [key]: current.spend,
                });
              }

              return total;
            },
            [] as any[]
          );
      }
      return finalAreaChart;
    }
    return [];
  }, [groupByLevel, groupByMetric, weeklyBrandHealthAreaChartSpend]);

  const { res: finalWeeklyBrandHealthAreaChartData } = useMemo(() => {
    if (weeklyBrandHealthSpendAreaChart.length > 0) {
      const groupByDateGqvData = groupChartDataByDate(
        weeklyBrandHealthSpendAreaChart,
        weeklySpendChartDateGrouping as DateGroupingKey,
        fiscalCal
      );
      return { res: groupByDateGqvData };
    }
    return { res: [] };
  }, [weeklySpendChartDateGrouping, weeklyBrandHealthSpendAreaChart, fiscalCal]);

  useEffect(() => {
    const areaChartEntry = finalWeeklyBrandHealthAreaChartData[0];
    const filteredAreaChartEntry = Object.fromEntries(
      Object.entries(areaChartEntry).filter(([key]) => key !== "date" && key !== "covariates")
    );
    const finalAreaChartKeys = Object.entries(filteredAreaChartEntry).map(([key, row], index) => ({
      name: key
        .split("_")
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" "),
      dataKey: key,
    }));

    setAreaChartKeys(finalAreaChartKeys);
  }, [finalWeeklyBrandHealthAreaChartData]);

  const excelDownloadWeeklyBrandHealth = useCallback(() => {
    exportToExcel(weeklyBrandHealthTimeSeriesByDate, "weekly_brand_health");
  }, [weeklyBrandHealthTimeSeriesByDate]);

  const excelDownloadWeeklySpend = useCallback(() => {
    exportToExcel(finalWeeklyBrandHealthAreaChartData, "weekly_spend");
  }, [finalWeeklyBrandHealthAreaChartData]);

  const excelDownloadModelOverview = useCallback(() => {
    exportToExcel([modelOverview], "model_overview");
  }, [modelOverview]);

  const pngDownloadWeeklyBrandHealth = useCallback(async () => {
    await downloadPNG(".brandEquityleft .chartContainer", "weekly_brand_health");
  }, []);

  const pngDownloadWeeklySpend = useCallback(async () => {
    await downloadPNG(".brandEquityleft > .chartContainer:nth-child(2)", "weekly_spend");
  }, []);

  const pngDownloadModelOverview = useCallback(async () => {
    await downloadPNG(".brandEquityOverview .brandEquityRight", "weekly_spend");
  }, []);

  return (
    <WidgetContainer
      collapsible
      header={"Overview"}
      subHeader={
        <>
          Assessing the impact of paid media on discrete brand health outcomes
          <InfoTooltip rightLabel="More info" size="sm">
            The goal is to understand and quantify the independent effect of paid media on a set of
            brand health outcomes. We employ a multivariate time series model that regresses each
            brand outcome on the set of paid media activities and a series of control variables. In
            the estimation, the model accounts for non-linearities in effects and the often
            significant timing differences between inputs and outputs.
          </InfoTooltip>
        </>
      }
    >
      {!R.isEmpty(modelOverview) &&
        !R.isEmpty(weeklyBrandHealthTimeSeriesByDate) &&
        !R.isEmpty(finalWeeklyBrandHealthAreaChartData) &&
        !R.isEmpty(areaChartKeys) && (
          <div className="brandEquityOverview">
            <div className="brandEquityleft">
              <ChartContainer
                enableHoverDesign
                rightActions={
                  <DownloadDropdown
                    size="sm"
                    onClickOptions={[excelDownloadWeeklyBrandHealth, pngDownloadWeeklyBrandHealth]}
                  />
                }
                title={
                  <>
                    <Dropdown
                      type={DropdownToggleType.WIDGET_TITLE}
                      value={weeklyBrandHealthChartDateGrouping}
                      options={DATE_GROUPING_OPTIONS_BRAND_EQUITY}
                      onChange={option => setWeeklyBrandHealthChartDateGrouping(option)}
                    />
                    <div>Brand Health</div>
                  </>
                }
                titleAfterDashText={`${dateToMddyy(
                  weeklyBrandHealthTimeSeries[0].date
                )} – ${dateToMddyy(
                  weeklyBrandHealthTimeSeries[weeklyBrandHealthTimeSeries.length - 1].date
                )}`}
                beforeTooltipText={isQv ? "Search Intent" : "Positive Response"}
                tooltipText={
                  isQv
                    ? `${queryVolumeLongLabel} (${queryVolumeShortLabel}) measures changes in the volume of branded search intent over time. We use ${queryVolumeShortLabel} to proxy for brand equity as it represents a funnel step between awareness and an actual site visit or install.`
                    : "Positive response indicates the percentage of survey respondents expressing favorable perceptions or experiences related to each brand health metric."
                }
              >
                <LineChart
                  customTooltipHeader={val => `Week of ${dateToMddyy(val)}`}
                  data={weeklyBrandHealthTimeSeriesByDate}
                  lineDataKeys={keysWithoutDate.map(key => ({
                    name: isQv
                      ? queryVolumeShortLabel
                      : String(
                          standardizeMetricName(
                            key,
                            false,
                            queryVolumeLongLabel,
                            queryVolumeShortLabel
                          )
                        )
                          .charAt(0)
                          .toUpperCase() +
                        String(
                          standardizeMetricName(
                            key,
                            false,
                            queryVolumeLongLabel,
                            queryVolumeShortLabel
                          )
                        ).slice(1),
                    dataKey: key,
                    toolTipText: isQv
                      ? `${queryVolumeLongLabel} (${queryVolumeShortLabel}) measures changes in the volume of branded search intent over time. We use ${queryVolumeShortLabel} to proxy for brand equity as it represents a funnel step between awareness and an actual site visit or install.`
                      : "ToolTipText",
                  }))}
                  colorMappings={keysWithoutDate.map((key, index) => ({
                    name: isQv
                      ? queryVolumeShortLabel
                      : String(
                          standardizeMetricName(
                            key,
                            false,
                            queryVolumeLongLabel,
                            queryVolumeShortLabel
                          )
                        )
                          .charAt(0)
                          .toUpperCase() +
                        String(
                          standardizeMetricName(
                            key,
                            false,
                            queryVolumeLongLabel,
                            queryVolumeShortLabel
                          )
                        ).slice(1),
                    color: colors[index],
                  }))}
                  tooltipLabelFormatter={val =>
                    isQv
                      ? queryVolumeShortLabel
                      : `${val === "Awareness" ? "Aided Awareness" : capitalizeWords(val)}`
                  }
                  tooltipFormatter={val =>
                    isQv ? val.toFixed(2) : `${numberFormatter.format(val, 0)}%`
                  }
                  tooltipShape="line"
                  usePercentageYAxis={!isQv}
                  useLeftYAxis={isQv}
                  xAxisDataKey="date"
                  xAxisTickFormatter={val => dateToMddyy(val)}
                />
              </ChartContainer>
              <ChartContainer
                enableHoverDesign
                rightActions={
                  <DownloadDropdown
                    size="sm"
                    onClickOptions={[excelDownloadWeeklySpend, pngDownloadWeeklySpend]}
                  />
                }
                title={
                  <>
                    <Dropdown
                      type={DropdownToggleType.WIDGET_TITLE}
                      value={weeklySpendChartDateGrouping}
                      options={DATE_GROUPING_OPTIONS_BRAND_EQUITY}
                      onChange={option => setWeeklySpendChartDateGrouping(option)}
                    />
                    <div>Spend</div>
                  </>
                }
                titleAfterDashText={`${dateToMddyy(
                  weeklyBrandHealthSpendAreaChart[0].date
                )} – ${dateToMddyy(
                  weeklyBrandHealthSpendAreaChart[weeklyBrandHealthSpendAreaChart.length - 1].date
                )}`}
              >
                <AreaChart
                  data={finalWeeklyBrandHealthAreaChartData}
                  xAxisDataKey="date"
                  xAxisTickFormatter={val => dateToMddyy(val)}
                  dateGrouping="Week"
                  yAxisWidth={75}
                  yAxisTickFormatter={val => `${currencyFormatter.format(val, 0)}`}
                  tooltipFormatter={val => {
                    if (!val) {
                      return val;
                    }
                    return val < 1000
                      ? currencyFormatter.format(val, 0)
                      : currencyFormatter.format(val, 1);
                  }}
                  tooltipShape="line"
                  areas={areaChartKeys}
                ></AreaChart>
              </ChartContainer>
            </div>
            <div className="brandEquityRight">
              <DynamicModelOverview
                sections={[
                  {
                    title: "Training Data",
                    rows: [
                      {
                        label: "First model observation",
                        value: dateToMddyy(modelOverview.train_start_date),
                      },
                      {
                        label: "Last model observation",
                        value: dateToMddyy(modelOverview.test_end_date),
                      },
                      {
                        label: "Average weekly spend",
                        value: currencyFormatter.format(modelOverview.mean_spend, 0),
                      },
                      {
                        label: "Outcome variable",
                        value: formatModelOverviewOutcomeVariable(groupByMetricName),
                      },
                    ],
                  },
                  {
                    title: "Recency",
                    rows: [
                      {
                        label: "Last refresh date",
                        value: dateToMddyy(modelOverview.refresh_date),
                      },
                    ],
                  },
                  {
                    title: "Statistics",
                    rows: [
                      {
                        label: "Out-of-sample MAPE",
                        value: percFormat(modelOverview.test_mape),
                        tooltipText:
                          "The percentage of variation in the unseen test data outcomes that is accounted for by the model's explanatory variables. Formally, this is 1 - (residual sum of squares) / (total sum of squares)",
                      },
                      {
                        label: "Out-of-sample MAE",
                        value: formatNumber(modelOverview.test_mae, 2),
                        tooltipText:
                          "The percentage of variation in the unseen test data outcomes that is accounted for by the model's explanatory variables. Formally, this is 1 - (residual sum of squares) / (total sum of squares)",
                      },
                    ],
                  },
                  {
                    title: "Key Results",
                    rows: [
                      {
                        label: "Incremental revenue from media-driven brand equity",
                        value: null,
                      },
                      {
                        label: "TTM Incremental Revenue:",
                        value: `$${(
                          incrementalRoasTakeaways.spendInducedBrandRevenue / 1000000
                        ).toFixed(2)}M`,
                      },
                      {
                        label: "TTM Media Spend:",
                        value: `$${(incrementalRoasTakeaways.paidMediaSpend / 1000000).toFixed(
                          2
                        )}M`,
                      },
                      {
                        label: "TTM Incremental ROAS:",
                        value: incrementalRoasTakeaways.iRoasTtm.toFixed(2),
                      },
                    ],
                  },
                ]}
                rightActions={
                  <DownloadDropdown
                    size="sm"
                    onClickOptions={[excelDownloadModelOverview, pngDownloadModelOverview]}
                    menuOptions={["XLSX", "PNG"]}
                  />
                }
              />
            </div>
          </div>
        )}
    </WidgetContainer>
  );
};

export default Overview;
